import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  agendaSubItemKindVariant,
  meetingStatusVariant,
  meetingTypeVariant,
} from '@/lib/status-variants'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Users,
  User,
  UserPlus,
  Edit,
  Copy,
  Send,
  Repeat,
  Play,
  CheckCircle2,
  XCircle,
  FileText,
  Link as LinkIcon,
  Plus,
  Video,
  StopCircle,
  RotateCcw,
  Loader2,
  NotebookPen,
  MoreVertical,
  CalendarPlus,
  Trash2,
  Tag,
  Lock,
} from 'lucide-react'

import { Link } from 'react-router-dom'
import useRouter from '../../hooks/useRouter'
import { useModuleActions } from '../../hooks/useMyPermissions'
import * as Query from '../../api/gql/query'
import * as Mutation from '../../api/gql/mutation'

import MeetingModal from './components/MeetingModal'
import MeetingNotesDialog from './components/MeetingNotesDialog'
import OccurrenceNavigator from './components/OccurrenceNavigator'
import ReadOnlyOccurrenceBanner from './components/ReadOnlyOccurrenceBanner'
import ActionItemModal from './components/ActionItemModal'
import DistributeMeetingDialog from './components/DistributeMeetingDialog'
import SendInvitationDialog from './components/SendInvitationDialog'
import FinalizeOccurrenceDialog from './components/FinalizeOccurrenceDialog'
import ActionItemsTable from './components/ActionItemsTable'
import ActionItemsStats from './components/ActionItemsStats'
import MeetingAttendancePanel from './components/MeetingAttendancePanel'
import MeetingFileRow from './components/MeetingFileRow'
import { formatDateInTz, formatDateTimeInTz, formatDateTimeLocal } from '../../utils/timezone'

// Wall-clock formatters bound to the meeting's project timezone — instantiated
// inside the component once `project.resolvedTimezone` is known.

const MeetingDetailSkeleton: React.FC = () => (
  <div className="space-y-4">
    <Skeleton className="h-8 w-64" />
    <Skeleton className="h-4 w-96" />
    <div className="grid grid-cols-2 gap-4">
      <Skeleton className="h-32" />
      <Skeleton className="h-32" />
    </div>
    <Skeleton className="h-64" />
  </div>
)

const MeetingDetail: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { match, history, query } = useRouter()
  const meetingId = (match.params as any).id
  const projectId = query.projectId as string | undefined

  const { canCreate, canEdit, canDelete, canApprove } = useModuleActions(
    'MEETINGS',
    projectId
  )

  const explicitOccurrenceNumber = query.occurrence ? Number(query.occurrence) : null

  const { data, loading, refetch } = useQuery(Query.MEETING, {
    variables: { id: meetingId },
    skip: !meetingId,
  })

  const meeting = data?.meeting
  const projectTz: string | undefined = meeting?.project?.resolvedTimezone || undefined
  const formatDateTime = (iso?: string | null) =>
    iso ? formatDateTimeInTz(iso, projectTz, i18n.language) : '—'
  const formatDate = (iso?: string | null) =>
    iso ? formatDateInTz(iso, projectTz, 'PP', i18n.language) : '—'
  // Audit timestamps (activity, minutes recorded, distributed) show in the
  // viewer's local time, not the project timezone.
  const formatAudit = (iso?: string | null) =>
    iso ? formatDateTimeLocal(iso, i18n.language) : '—'

  // For a closed series (COMPLETED/CANCELLED) with at least one finalized
  // occurrence, default to viewing the last one even if the URL doesn't
  // carry `?occurrence=`. Computed during render to avoid a flash of the
  // live action bar before the URL effect re-writes the param.
  const seriesClosedStatus =
    meeting?.status === 'COMPLETED' || meeting?.status === 'CANCELLED'
  const shouldDefaultToLast =
    explicitOccurrenceNumber === null &&
    seriesClosedStatus &&
    (meeting?.occurrenceCount ?? 0) >= 1

  const viewingOccurrenceNumber =
    explicitOccurrenceNumber ??
    (shouldDefaultToLast ? meeting!.occurrenceCount : null)

  const viewingOccurrence = viewingOccurrenceNumber
    ? (meeting?.occurrences || []).find((o: any) => o.occurrenceNumber === viewingOccurrenceNumber)
    : null

  const { data: occurrenceData } = useQuery(Query.MEETING_OCCURRENCE, {
    variables: { id: viewingOccurrence?.id },
    skip: !viewingOccurrence?.id,
  })

  const isReadOnly = viewingOccurrenceNumber !== null
  const snapshot = occurrenceData?.meetingOccurrence

  const navigateToOccurrence = (occurrenceNumber: number | null) => {
    const base = match.url
    if (occurrenceNumber === null) history.push(base)
    else history.push(`${base}?occurrence=${occurrenceNumber}`)
  }
  const [editOpen, setEditOpen] = useState(false)
  const [notesOpen, setNotesOpen] = useState(false)
  const [actionModalOpen, setActionModalOpen] = useState(false)
  const [editingActionItem, setEditingActionItem] = useState<string | null>(null)
  const [distributeOpen, setDistributeOpen] = useState(false)
  const [invitationOpen, setInvitationOpen] = useState(false)
  const [finalizeOccurrenceOpen, setFinalizeOccurrenceOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [attendancePending, setAttendancePending] = useState(false)

  // Sync URL so it matches the defaulted occurrence — keeps share/back behavior consistent.
  React.useEffect(() => {
    if (!shouldDefaultToLast || !meeting) return
    history.replace(`${match.url}?occurrence=${meeting.occurrenceCount}`)
  }, [shouldDefaultToLast, meeting?.id, meeting?.occurrenceCount])

  const [startMeeting, { loading: starting }] = useMutation(Mutation.START_MEETING)
  const [cancelMeeting, { loading: cancelling }] = useMutation(Mutation.CANCEL_MEETING)
  const [reopenMeeting, { loading: reopening }] = useMutation(Mutation.REOPEN_MEETING)
  const [stopRecurrence, { loading: stoppingRecurrence }] = useMutation(Mutation.STOP_RECURRENCE)
  const [deleteActionItem] = useMutation(Mutation.DELETE_ACTION_ITEM, {
    refetchQueries: ['meeting'],
  })
  const [deleteMeeting, { loading: deletingMeeting }] = useMutation(
    Mutation.DELETE_MEETING
  )
  const [duplicateMeeting, { loading: duplicatingMeeting }] = useMutation(
    Mutation.DUPLICATE_MEETING
  )

  const handleDuplicate = async () => {
    if (!meeting) return
    try {
      const res = await duplicateMeeting({ variables: { id: meeting.id } })
      const newId = res.data?.duplicateMeeting?.id
      toastr.success(t('Common_success'), t('Meeting_duplicated'))
      if (newId) {
        history.push(match.url.replace(/\/[^/]+$/, `/${newId}`))
      }
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  const handleDelete = async () => {
    if (!meeting) return
    try {
      await deleteMeeting({ variables: { id: meeting.id } })
      toastr.success(t('Common_success'), t('Meeting_deleted'))
      setDeleteOpen(false)
      history.push(match.url.replace(/\/[^/]+$/, ''))
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  if (loading && !meeting) return <MeetingDetailSkeleton />
  if (!meeting) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        {t('Meeting_not_found')}
      </div>
    )
  }

  const handleTransition = async (fn: any, label: string) => {
    try {
      await fn({ variables: { id: meeting.id } })
      toastr.success(t('Common_success'), label)
      refetch()
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  const handleStopRecurrence = async () => {
    try {
      await stopRecurrence({ variables: { id: meeting.id } })
      toastr.success(t('Common_success'), t('Meeting_recurrence_stopped'))
      refetch()
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  const baselineEmails: string[] = Array.from(
    new Set(
      [
        meeting.organizer?.email,
        ...(meeting.attendees || []).map((a: any) => a.email),
        ...(Array.isArray(meeting.externalGuests)
          ? meeting.externalGuests.map((g: any) => g.email)
          : []),
      ].filter(Boolean)
    )
  )

  const isActive = meeting.status === 'SCHEDULED' || meeting.status === 'IN_PROGRESS'
  const isCompleted = meeting.status === 'COMPLETED'
  const isCancelled = meeting.status === 'CANCELLED'
  const seriesClosed = isCompleted || isCancelled
  const headerOccurrenceNumber = seriesClosed && meeting.occurrenceCount > 0
    ? meeting.occurrenceCount
    : meeting.occurrenceCount + 1
  const canFinalize =
    !isCancelled &&
    (meeting.status === 'IN_PROGRESS' ||
      (meeting.status === 'COMPLETED' && meeting.recurrence !== 'NONE'))

  const agendaForDisplay =
    isReadOnly && snapshot
      ? ((snapshot.agendaSnapshot as any[]) || []).map((a: any) => {
          const entries = (a.subItems || []).map((s: any) => ({
            id: s.id,
            kind: s.kind,
            status: s.status,
            title: s.title,
            orderIndex: s.orderIndex ?? 0,
            assignedTo: s.assignedToName ? { id: s.assignedToId, name: s.assignedToName } : null,
          }))
          return {
            id: a.agendaItemId,
            title: a.title,
            responsible: a.responsibleName ? { id: a.responsibleId, name: a.responsibleName } : null,
            entries,
          }
        })
      : (meeting.agendaItems || []).map((a: any) => {
          const subs = (a.subItems || [])
            .filter((s: any) => s.kind !== 'TASK')
            .map((s: any) => ({
              id: s.id,
              kind: s.kind,
              status: s.status,
              title: s.title,
              orderIndex: s.orderIndex ?? 0,
              assignedTo: s.assignedTo,
            }))
          const acts = (a.actionItems || []).map((ai: any) => ({
            id: ai.id,
            kind: 'TASK' as const,
            status: ai.status,
            title: ai.title,
            orderIndex: ai.agendaOrderIndex ?? 0,
            assignedTo: ai.assignedTo,
          }))
          const entries = [...subs, ...acts].sort((x, y) => x.orderIndex - y.orderIndex)
          return { ...a, entries }
        })

  // A finalized occurrence reads its files from the snapshot (id + name only,
  // plus the agenda item they hang on); a live meeting shows both levels the
  // same way, so the section doesn't change shape after finalizing.
  const filesForDisplay =
    isReadOnly && snapshot
      ? ((snapshot.filesSnapshot as any[]) || []).map((f: any) => ({
          id: f.fileId,
          filename: f.name,
          size: 0,
          source: f.source || null,
        }))
      : [
          ...(meeting.files || []),
          ...(meeting.agendaItems || []).flatMap((a: any) =>
            (a.files || []).map((f: any) => ({ ...f, source: a.title })),
          ),
        ]

  const scheduledAtForDisplay =
    isReadOnly && snapshot ? snapshot.scheduledAt : meeting.scheduledAt

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => history.push(match.url.replace(/\/[^/]+$/, ''))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="text-xs font-mono text-muted-foreground">
          {meeting.displayNumber}
          {(meeting.occurrenceCount > 0 || meeting.recurrence !== 'NONE') &&
            `.${viewingOccurrenceNumber ?? headerOccurrenceNumber}`}
        </span>
        <h1 className="text-xl font-bold flex-1 truncate">{meeting.title}</h1>
        <Badge variant={meetingTypeVariant(meeting.type)} className="gap-1">
          <Tag className="h-3 w-3" />
          {t(`Meeting_type_${meeting.type.toLowerCase()}`)}
        </Badge>
        <Badge variant={meetingStatusVariant(meeting.status)}>
          {t(`Meeting_status_${meeting.status.toLowerCase()}`)}
        </Badge>
        {meeting.confidential && (
          <Badge variant="neutral" className="gap-1">
            <Lock className="h-3 w-3" />
            {t('Meeting_confidential_badge')}
          </Badge>
        )}
        {((!isReadOnly && canEdit) || canCreate || canApprove || canDelete) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" title={t('Common_more')}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!isReadOnly && canEdit && (
                <DropdownMenuItem
                  onSelect={() => setEditOpen(true)}
                  disabled={isCancelled || isCompleted}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {t('Common_edit')}
                </DropdownMenuItem>
              )}
              {canCreate && (
                <DropdownMenuItem
                  onSelect={handleDuplicate}
                  disabled={duplicatingMeeting}
                >
                  {duplicatingMeeting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  {t('Meeting_action_duplicate')}
                </DropdownMenuItem>
              )}
              {canApprove && (
                <>
                  <DropdownMenuSeparator />
                  {seriesClosed ? (
                    <DropdownMenuItem
                      onSelect={async () => {
                        await handleTransition(reopenMeeting, t('Meeting_reopened'))
                        if (isReadOnly) navigateToOccurrence(null)
                      }}
                      disabled={reopening}
                      className="text-blue-700 focus:text-blue-700 focus:bg-blue-50"
                    >
                      {reopening ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RotateCcw className="h-4 w-4 mr-2" />
                      )}
                      {t('Meeting_action_reopen')}
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onSelect={() =>
                        handleTransition(cancelMeeting, t('Meeting_cancelled'))
                      }
                      disabled={cancelling}
                      className="text-red-700 focus:text-red-700 focus:bg-red-50"
                    >
                      {cancelling ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <XCircle className="h-4 w-4 mr-2" />
                      )}
                      {t('Meeting_action_cancel')}
                    </DropdownMenuItem>
                  )}
                </>
              )}
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault()
                      setDeleteOpen(true)
                    }}
                    disabled={deletingMeeting}
                    className="text-red-700 focus:text-red-700 focus:bg-red-50"
                  >
                    {deletingMeeting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    {t('Meeting_action_delete')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {meeting.occurrences && meeting.occurrences.length > 0 && (
        <OccurrenceNavigator
          pastOccurrences={meeting.occurrences.map((o: any) => ({
            occurrenceNumber: o.occurrenceNumber,
            scheduledAt: o.scheduledAt,
          }))}
          currentNumber={meeting.occurrenceCount + 1}
          currentScheduledAt={meeting.scheduledAt}
          seriesClosed={seriesClosed}
          viewingNumber={viewingOccurrenceNumber}
          onNavigate={navigateToOccurrence}
          timezone={projectTz}
        />
      )}

      {isReadOnly && snapshot && (
        <ReadOnlyOccurrenceBanner
          occurrenceNumber={snapshot.occurrenceNumber}
          scheduledAt={snapshot.scheduledAt}
          finalizedAt={snapshot.finalizedAt}
          finalizedBy={snapshot.finalizedBy}
          currentNumber={meeting.occurrenceCount + 1}
          seriesClosed={seriesClosed}
          onBackToCurrent={() => navigateToOccurrence(null)}
          timezone={projectTz}
          minutesPdfUrl={snapshot.minutesPdfUrl}
        />
      )}

      {/* Action bar */}
      {!isReadOnly && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-800"
            onClick={() => setNotesOpen(true)}
            disabled={isCancelled || isCompleted}
          >
            <NotebookPen className="h-4 w-4" />
            {t('Meeting_action_take_notes')}
          </Button>
          {meeting.status === 'SCHEDULED' && canApprove && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800"
                onClick={() => setInvitationOpen(true)}
              >
                <CalendarPlus className="h-4 w-4" />
                {meeting.invitationsSentAt
                  ? t('Meeting_action_resend_invitation')
                  : t('Meeting_action_send_invitation')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTransition(startMeeting, t('Meeting_started'))}
                disabled={starting}
              >
                {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                {t('Meeting_action_start')}
              </Button>
            </>
          )}
          {canFinalize && canApprove && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFinalizeOccurrenceOpen(true)}
              disabled={attendancePending}
              title={attendancePending ? t('Meeting_attendance_saving') : undefined}
            >
              <CheckCircle2 className="h-4 w-4" />
              {t('Meeting_action_finalize')}
            </Button>
          )}
          {isCompleted && canApprove && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDistributeOpen(true)}
            >
              <Send className="h-4 w-4" />
              {t('Meeting_action_distribute')}
            </Button>
          )}
        </div>
      )}

      {/* Info + Attendees side-by-side on large screens */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('Meeting_section_info')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>{formatDateTime(scheduledAtForDisplay)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>{meeting.durationMinutes} min</span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              {meeting.isVirtual ? (
                <Video className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              {meeting.isVirtual ? (
                meeting.virtualLink ? (
                  <a
                    href={meeting.virtualLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline truncate"
                  >
                    {meeting.virtualLink}
                  </a>
                ) : (
                  <span>{t('Meeting_virtual_label')}</span>
                )
              ) : (
                <span className="truncate">{meeting.location || '—'}</span>
              )}
            </div>
            {meeting.onlineSyncError === 'reauth_required' && (
              <div className="flex items-center gap-2 min-w-0 col-span-full">
                <span className="text-sm text-amber-600">
                  {t('Meeting_teams_reauth')}{' '}
                  <a href="/account" className="underline">{t('Account_connect')}</a>
                </span>
              </div>
            )}
            {meeting.organizer && (
              <div className="flex items-center gap-2 min-w-0">
                <User className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="truncate">
                  <span className="text-muted-foreground">{t('Meeting_field_organizer')}:</span>{' '}
                  {meeting.organizer.name}
                </span>
              </div>
            )}
            {meeting.recurrence !== 'NONE' && (
              <div className="flex items-center gap-2 sm:col-span-2">
                <Repeat className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="truncate">
                  {t(`Meeting_recurrence_${meeting.recurrence.toLowerCase()}`)}
                  {meeting.recurrenceUntil &&
                    ` · ${t('Meeting_recurrence_until_short')} ${formatDate(meeting.recurrenceUntil)}`}
                </span>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-1.5"
                    onClick={handleStopRecurrence}
                    disabled={stoppingRecurrence}
                    title={t('Meeting_action_stop_recurrence')}
                  >
                    {stoppingRecurrence ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <StopCircle className="h-3.5 w-3.5" />
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <MeetingAttendancePanel
          meeting={meeting}
          projectId={meeting.project.id}
          readOnly={isReadOnly}
          snapshotAttendees={snapshot?.attendeesSnapshot}
          snapshotGuests={snapshot?.externalGuestsSnapshot}
          onPendingChange={setAttendancePending}
          canEdit={canEdit}
        />
      </div>

      {/* Agenda */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('Meeting_section_agenda')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(agendaForDisplay || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('Meeting_agenda_empty')}</p>
          ) : (
            (agendaForDisplay || []).map((a: any) => (
              <div key={a.id} className="border rounded-lg p-3">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="font-medium">{a.title}</h4>
                  <div className="flex items-center gap-2 shrink-0">
                    {(a.entries?.length || 0) > 0 && (
                      <Badge
                        variant="outline"
                        className="text-xs font-normal border-purple-200 bg-purple-50 text-purple-700"
                      >
                        {t('AgendaSubItem_count', { count: a.entries.length })}
                      </Badge>
                    )}
                    {a.responsible && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <User className="h-3 w-3" />
                        {a.responsible.name}
                      </div>
                    )}
                  </div>
                </div>
                {(a.entries?.length || 0) > 0 && (
                  <ul className="mt-2 space-y-1">
                    {a.entries.map((s: any) => (
                      <li
                        key={s.id}
                        className="flex items-center gap-2 text-sm text-muted-foreground"
                      >
                        <Badge
                          variant={agendaSubItemKindVariant(s.kind)}
                          className="text-[10px] font-medium uppercase tracking-wide py-0 px-1.5"
                        >
                          {t(`AgendaSubItem_kind_${s.kind.toLowerCase()}`)}
                        </Badge>
                        <span
                          className={
                            s.status === 'RESOLVED' || s.status === 'CLOSED'
                              ? 'line-through opacity-60'
                              : ''
                          }
                        >
                          {s.title || <span className="italic">—</span>}
                        </span>
                        {s.assignedTo && (
                          <span className="text-xs opacity-70">
                            · {s.assignedTo.name}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Action items */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>{t('Meeting_section_action_items')}</span>
            {canCreate && !isCompleted && !isReadOnly && (
              <Button
                size="sm"
                onClick={() => {
                  setEditingActionItem(null)
                  setActionModalOpen(true)
                }}
              >
                <Plus className="h-4 w-4" />
                {t('ActionItem_new_button')}
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {(meeting.actionItems || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('ActionItem_empty_hint')}</p>
          ) : (
            <>
              <ActionItemsStats items={meeting.actionItems} />
              <ActionItemsTable
                items={meeting.actionItems}
                onEdit={
                  canEdit && !isCompleted && !isReadOnly
                    ? (id) => {
                        setEditingActionItem(id)
                        setActionModalOpen(true)
                      }
                    : undefined
                }
                onDelete={
                  canDelete && !isCompleted && !isReadOnly
                    ? async (id) => {
                        try {
                          await deleteActionItem({ variables: { id } })
                          toastr.success(t('Common_success'), t('ActionItem_deleted'))
                        } catch (e: any) {
                          toastr.error(t('Common_error'), e.message)
                        }
                      }
                    : undefined
                }
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Files */}
      {(filesForDisplay || []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('Meeting_section_files')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {/* MeetingFileRow falls back to the auth-gated download route when
                no presigned url is present — finalized occurrences read their
                files from the snapshot, which only carries id + name. */}
            {(filesForDisplay || []).map((f: any) => (
              <MeetingFileRow
                key={f.id}
                file={f}
                className="border-transparent"
                action={
                  f.source ? (
                    <span className="shrink-0 max-w-[40%] truncate text-xs text-muted-foreground">
                      {f.source}
                    </span>
                  ) : undefined
                }
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Linked items */}
      {((meeting.linkedSnags || []).length > 0 ||
        (meeting.linkedDocs || []).length > 0 ||
        (meeting.linkedTenders || []).length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('Meeting_section_linked_items')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {(meeting.linkedSnags || []).map((s: any) => (
              <Link
                key={s.id}
                to={`/project-setup/${meeting.project.id}/snags?tab=list&openSnagId=${s.id}`}
                className="flex items-center gap-2 hover:bg-muted/40 rounded px-1 py-0.5 -mx-1"
              >
                <LinkIcon className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs uppercase text-muted-foreground">
                  {t('Meeting_link_kind_snag')}
                </span>
                {(s.displayNumber || s.number) && (
                  <span className="font-mono text-xs text-muted-foreground">
                    {s.displayNumber || s.number}
                  </span>
                )}
                <span className="text-primary hover:underline">{s.title}</span>
              </Link>
            ))}
            {(meeting.linkedDocs || []).map((d: any) => (
              <Link
                key={d.id}
                to={`/project/${meeting.project.id}/documents?documentId=${d.id}`}
                className="flex items-center gap-2 hover:bg-muted/40 rounded px-1 py-0.5 -mx-1"
              >
                <FileText className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs uppercase text-muted-foreground">
                  {t('Meeting_link_kind_doc')}
                </span>
                <span className="text-primary hover:underline">{d.name}</span>
              </Link>
            ))}
            {(meeting.linkedTenders || []).map((tn: any) => (
              <Link
                key={tn.id}
                to={`/project/${meeting.project.id}/tendering/tenders/${tn.id}`}
                className="flex items-center gap-2 hover:bg-muted/40 rounded px-1 py-0.5 -mx-1"
              >
                <LinkIcon className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs uppercase text-muted-foreground">
                  {t('Meeting_link_kind_tender')}
                </span>
                <span className="text-primary hover:underline">{tn.name}</span>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Activity log */}
      {(meeting.activityLogs || []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('Meeting_section_activity')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {(meeting.activityLogs || []).map((log: any) => (
              <div key={log.id} className="flex items-center gap-2 text-muted-foreground">
                <span className="text-xs">{formatAudit(log.createdAt)}</span>
                <span className="font-medium text-foreground">{log.title}</span>
                {log.description && <span>· {log.description}</span>}
                {log.user && <span className="text-xs">· {log.user.name}</span>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {editOpen && (
        <MeetingModal
          isOpen={editOpen}
          onClose={(saved) => {
            setEditOpen(false)
            if (saved) refetch()
          }}
          meetingId={meeting.id}
          projectId={meeting.project.id}
        />
      )}

      {notesOpen && (
        <MeetingNotesDialog
          isOpen={notesOpen}
          onClose={() => {
            setNotesOpen(false)
            refetch()
          }}
          meetingId={meeting.id}
        />
      )}

      {actionModalOpen && (
        <ActionItemModal
          isOpen={actionModalOpen}
          onClose={(saved) => {
            setActionModalOpen(false)
            setEditingActionItem(null)
            if (saved) refetch()
          }}
          actionItemId={editingActionItem}
          meetingId={editingActionItem ? null : meeting.id}
        />
      )}

      {distributeOpen && (
        <DistributeMeetingDialog
          isOpen={distributeOpen}
          onClose={() => {
            setDistributeOpen(false)
            refetch()
          }}
          confidential={!!meeting.confidential}
          meetingId={meeting.id}
          baselineEmails={baselineEmails}
          alreadySentAt={meeting.distributedAt ? formatAudit(meeting.distributedAt) : null}
        />
      )}

      {invitationOpen && (
        <SendInvitationDialog
          isOpen={invitationOpen}
          onClose={() => {
            setInvitationOpen(false)
            refetch()
          }}
          meetingId={meeting.id}
          baselineEmails={baselineEmails}
          alreadySent={!!meeting.invitationsSentAt}
        />
      )}

      <FinalizeOccurrenceDialog
        open={finalizeOccurrenceOpen}
        onClose={() => setFinalizeOccurrenceOpen(false)}
        meeting={{
          id: meeting.id,
          recurrence: meeting.recurrence,
          recurrenceUntil: meeting.recurrenceUntil,
          scheduledAt: meeting.scheduledAt,
          occurrenceCount: meeting.occurrenceCount ?? 0,
        }}
        onFinalized={(occurrenceNumber) => {
          refetch()
          navigateToOccurrence(occurrenceNumber)
        }}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('Meeting_action_delete')}</DialogTitle>
            <DialogDescription>
              {t('Meeting_delete_confirm', { title: meeting.title })}
            </DialogDescription>
          </DialogHeader>
          {(meeting.occurrenceCount ?? 0) > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {t('Meeting_delete_warning_occurrences', { count: meeting.occurrenceCount })}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deletingMeeting}
            >
              {t('Common_cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deletingMeeting}
            >
              {deletingMeeting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {t('Meeting_action_delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  )
}

export default MeetingDetail
